import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Checkbox, CircularProgress, ListItem, Tooltip, useTheme } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { DeleteIcon, ResetIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import LazyWrapper from 'app/shared/components/common/LazyWrapper';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT_FOR_TREE_ITEM } from 'constants/date';
import { PAGE_CODES } from 'constants/pageCodes';
import { checkIsMalwareBlocked, checkIsTitlePortalUpload } from 'utils/documents';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  deleteDocumentAction,
  getDocumentsAction,
  restoreDocumentAction,
  toggleDocumentSelectionAction,
} from 'store/documents/actions';
import { getIsDocumentsSelected } from 'store/documents/selectors';
import { openConfirmationDialogAction, showMessageAction } from 'store/global/actions';
import { selectIsLoanCanceled } from 'store/loanApplications/selectors';

import {
  DOCUMENT_LABEL_TOOLTIP_POPPER_PROPS,
  LoanDocumentsTreeNodeType,
} from '../../../LoanApplicationDocuments.constants';
import { LoanApplicationDocumentsTestIds } from '../../../LoanApplicationDocuments.testIds';

import DocumentDiscriminatorTag from './DocumentDiscriminatorTag';
import { documentDiscriminatorPropType } from './DocumentDiscriminatorTag/DocumentDiscriminatorTag.constants';
import { SIGNED_DOCUMENT_DELETE_TOOLTIP } from './DocumentItem.constants';
import { getDocumentItemStyles } from './DocumentItem.styles';
import DocumentMalwareTag from './DocumentMalwareTag';
import DocumentRecipientCopiesToggle from './DocumentRecipientCopiesToggle';
import DocumentRecipientCopyTag from './DocumentRecipientCopyTag';
import DocumentUploadSourceTag from './DocumentUploadSourceTag';

const DocumentItem = ({
  id,
  name,
  createdDate,
  pagesCount,
  loanParams,
  documentId = '',
  onDocumentItemClick,
  isActive,
  toBeMerged,
  onDragStart,
  onDragEnd,
  typeId,
  isDragging,
  isDeleted,
  isCanBeDeleted,
  isRejected = false,
  onCustomNavigateDocument,
  onOpenDocumentItemContextMenu,
  isSelectable = false,
  discriminator = null,
  isMalware = null,
  malwareScanTimeUtc = null,
  uploadSource = null,
  companyId = null,
  uploadedByUserId = null,
  isRecipientCopy = false,
  recipientSignerName = null,
  recipientCopiesCount = 0,
  isRecipientCopiesExpanded = false,
  isNested = false,
  children = null,
  onToggleRecipientCopies,
}) => {
  const isDeletedNotRejected = isDeleted && !isRejected;
  const isDragDisabled = isDeleted || isRecipientCopy;
  const discriminatorValue = discriminator?.value;
  const isMalwareBlocked = checkIsMalwareBlocked(isMalware);
  const isDocumentSelectable = isSelectable && !isMalwareBlocked;
  const isTitlePortalUpload = checkIsTitlePortalUpload(uploadSource);

  const theme = useTheme();

  const classes = getDocumentItemStyles(theme, {
    isDeleted: isDeletedNotRejected,
    isSelectable: isDocumentSelectable,
    isNested,
  });
  const dispatch = useDispatch();

  const [isLoadingDelete, setLoadingDelete] = useState([]);

  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.DOCUMENTS));
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isDocumentSelected = useSelector(getIsDocumentsSelected(id));

  const isDeleteButtonDisabled = !isDeleted && !isCanBeDeleted;
  const deleteButtonTitle = isDeleteButtonDisabled ? SIGNED_DOCUMENT_DELETE_TOOLTIP : 'Delete';

  const treeItemSx = useMemo(
    () => ({
      ...classes.root,
      '& .MuiTreeItem-label': classes.typeLabel,
      '& .MuiTreeItem-iconContainer': classes.iconContainer,
    }),
    [classes],
  );

  const listItemSx = useMemo(
    () => [
      classes.documentTreeItem,
      toBeMerged && classes.toBeMerged,
      isDeleted && classes.isDeleted,
      { '&.Mui-selected': classes.selected },
    ],
    [classes, toBeMerged, isDeleted],
  );

  const onManageDocument = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      const { id: docId } = e.target.dataset;
      const action = isDeleted ? restoreDocumentAction : deleteDocumentAction;

      setLoadingDelete([...isLoadingDelete, docId]);

      dispatch(action({ documentId: docId }))
        .then(() => {
          if (!isDeleted) {
            dispatch(getDocumentsAction(loanParams));
          }

          if (isDeleted) {
            dispatch(showMessageAction({ message: 'Document restored', variant: 'success' }));
          }
        })
        .catch(Error)
        .finally(() => {
          setLoadingDelete(isLoadingDelete.filter(id => id !== docId));

          if (docId && documentId === docId && !isDeleted) {
            onCustomNavigateDocument('');
          }
        });
    },
    [isDeleted, dispatch, documentId, isLoadingDelete, loanParams, onCustomNavigateDocument],
  );

  const onShowConfirmationDialog = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Confirm document delete',
        description: 'Are you sure you want to delete this document?',
        onAccept: () => onManageDocument(e),
      };

      dispatch(openConfirmationDialogAction(params));
    },
    [onManageDocument, dispatch],
  );

  const stopPropagation = useCallback(e => e.stopPropagation(), []);

  const onToggleDocumentSelection = useCallback(
    e => {
      stopPropagation(e);
      dispatch(toggleDocumentSelectionAction(id));
    },
    [dispatch, stopPropagation, id],
  );

  return (
    <LazyWrapper height={55} isDragging={isDragging} key={id}>
      <TreeItem
        aria-label={name}
        itemId={id}
        label={
          <ListItem
            component="div"
            data-id={id}
            data-is-deleted={isDeleted}
            data-testid={LoanApplicationDocumentsTestIds.DOCUMENT_LIST_ITEM}
            data-tree-node-type={LoanDocumentsTreeNodeType.DOCUMENT_ITEM}
            key={id}
            selected={isActive}
            sx={listItemSx}
            onClick={onDocumentItemClick}
            onContextMenu={onOpenDocumentItemContextMenu}
          >
            <Box
              data-type-id={typeId}
              draggable={!isDragDisabled}
              id={id}
              sx={classes.draggable}
              onMouseDown={isDragDisabled ? null : onDragStart}
              onMouseUp={isDragDisabled ? null : onDragEnd}
            >
              {isDocumentSelectable && (
                <Checkbox
                  disableRipple
                  checked={isDocumentSelected}
                  data-testid={LoanApplicationDocumentsTestIds.getDocumentSelectCheckboxId(id)}
                  disabled={isRecipientCopy}
                  id={`select-document-${id}`}
                  size="small"
                  onClick={onToggleDocumentSelection}
                  onMouseDown={stopPropagation}
                />
              )}

              <div className="flex flex-col overflow-hidden">
                <Tooltip PopperProps={DOCUMENT_LABEL_TOOLTIP_POPPER_PROPS} title={name}>
                  <Box className="font-600" sx={classes.name}>
                    {isDeletedNotRejected && 'Deleted: '}
                    {name}
                  </Box>
                </Tooltip>

                <Box className="text-11" sx={classes.metaRow}>
                  <Box className="italic" component="span" sx={classes.metaDate}>
                    {dayjs(createdDate).format(COMMON_DATETIME_FORMAT_FOR_TREE_ITEM)}
                  </Box>
                  {!!discriminatorValue && (
                    <>
                      <Box aria-hidden component="span" sx={classes.metaSeparator}>
                        &middot;
                      </Box>
                      <DocumentDiscriminatorTag discriminator={discriminator} documentId={id} />
                    </>
                  )}
                  {isTitlePortalUpload && (
                    <>
                      <Box aria-hidden component="span" sx={classes.metaSeparator}>
                        &middot;
                      </Box>
                      <DocumentUploadSourceTag
                        companyId={companyId}
                        documentId={id}
                        uploadedByUserId={uploadedByUserId}
                      />
                    </>
                  )}
                  {isMalwareBlocked && (
                    <>
                      <Box aria-hidden component="span" sx={classes.metaSeparator}>
                        &middot;
                      </Box>
                      <DocumentMalwareTag
                        documentId={id}
                        isMalware={isMalware}
                        scanTimeUtc={malwareScanTimeUtc}
                      />
                    </>
                  )}
                </Box>

                {isRecipientCopy && (
                  <Box sx={classes.metaRow}>
                    <DocumentRecipientCopyTag documentId={id} signerName={recipientSignerName} />
                  </Box>
                )}

                {recipientCopiesCount > 0 && (
                  <Box sx={classes.metaRow}>
                    <DocumentRecipientCopiesToggle
                      count={recipientCopiesCount}
                      documentId={id}
                      isExpanded={isRecipientCopiesExpanded}
                      onToggle={onToggleRecipientCopies}
                    />
                  </Box>
                )}
              </div>

              <div className="flex flex-col items-center">
                <div
                  className="font-600"
                  data-testid={LoanApplicationDocumentsTestIds.getDocumentPagesCountId(pagesCount)}
                >
                  {`${pagesCount} p.`}
                </div>

                {isManageAllowed && !isLoanCanceled && (
                  <>
                    {isLoadingDelete.includes(id) ? (
                      <CircularProgress color="primary" size={25} />
                    ) : (
                      <ButtonWithTooltip
                        color="inherit"
                        data-id={id}
                        data-name={`${isDeleted ? 'restore' : 'delete'}-document-button`}
                        data-testid={isDeleted ? 'restore-document' : 'delete-document'}
                        disabled={isDeleteButtonDisabled}
                        icon={isDeleted ? ResetIcon : DeleteIcon}
                        iconButtonClassName="p-1"
                        iconClassName="tool__disable-interaction"
                        iconSize={IconSize[20]}
                        sx={classes.deleteButton}
                        title={isDeleted ? 'Restore' : deleteButtonTitle}
                        onClick={isDeleted ? onManageDocument : onShowConfirmationDialog}
                      />
                    )}
                  </>
                )}
              </div>
            </Box>
          </ListItem>
        }
        sx={treeItemSx}
      >
        {children}
      </TreeItem>
    </LazyWrapper>
  );
};

DocumentItem.propTypes = {
  loanParams: PropTypes.object.isRequired,
  typeId: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  createdDate: PropTypes.string.isRequired,
  pagesCount: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  toBeMerged: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isDeleted: PropTypes.bool.isRequired,
  isCanBeDeleted: PropTypes.bool.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDocumentItemClick: PropTypes.func.isRequired,
  onToggleRecipientCopies: PropTypes.func.isRequired,
  isMalware: PropTypes.bool,
  isRejected: PropTypes.bool,
  malwareScanTimeUtc: PropTypes.string,
  uploadSource: PropTypes.string,
  companyId: PropTypes.string,
  uploadedByUserId: PropTypes.string,
  documentId: PropTypes.string,
  isSelectable: PropTypes.bool,
  isRecipientCopy: PropTypes.bool,
  recipientSignerName: PropTypes.string,
  recipientCopiesCount: PropTypes.number,
  isRecipientCopiesExpanded: PropTypes.bool,
  isNested: PropTypes.bool,
  children: PropTypes.node,
  discriminator: documentDiscriminatorPropType,
  onCustomNavigateDocument: PropTypes.func,
  onOpenDocumentItemContextMenu: PropTypes.func,
};

export default DocumentItem;
